import { useCallback, useMemo } from 'react';

import { useRouteLoaderData } from '@remix-run/react';

import type { loader as communityLayoutLoader } from '../../community';
import { isConditionLock } from '../_utils/communityAccess';
import { useCommunityMembership } from './useCommunityMembership';

export type SpaceAccessTarget = { id: string };
export type SpacePostTarget = SpaceAccessTarget & { read_only: boolean };

export const useSpaceAccess = () => {
  const route = useRouteLoaderData<typeof communityLayoutLoader>('routes/community');
  const { permissions } = useCommunityMembership();
  const spaceAccess = route?.spaceAccess;

  const canAccessSpace = useCallback(
    (space: SpaceAccessTarget | null) => !space || spaceAccess?.[space.id]?.state === 'open',
    [spaceAccess],
  );

  // The spaces payload leaves out channels a gate hides, so "hidden" only marks a closed channel the viewer can't
  // enter: every tier gating it is archived.
  const isSpaceClosed = useCallback(
    (space: SpaceAccessTarget) => spaceAccess?.[space.id]?.state === 'hidden',
    [spaceAccess],
  );

  const isSpaceNew = useCallback((space: SpaceAccessTarget) => spaceAccess?.[space.id]?.is_new === true, [spaceAccess]);

  const isSpaceUnlisted = useCallback(
    (space: SpaceAccessTarget) => spaceAccess?.[space.id]?.unlisted === true,
    [spaceAccess],
  );

  const isSpacePlaced = useCallback(
    (space: SpaceAccessTarget) => spaceAccess?.[space.id]?.placed === true,
    [spaceAccess],
  );

  const isSpacePreviewable = useCallback(
    (space: SpaceAccessTarget) => spaceAccess?.[space.id]?.previewable === true,
    [spaceAccess],
  );

  const listsMembers = useCallback(
    (space: SpaceAccessTarget) => spaceAccess?.[space.id]?.lists_members === true,
    [spaceAccess],
  );

  const isSpaceConditionLocked = useCallback(
    (space: SpaceAccessTarget) => isConditionLock(spaceAccess?.[space.id]?.reason),
    [spaceAccess],
  );

  const lapsedSubscriptionFor = useCallback(
    (space: SpaceAccessTarget) => spaceAccess?.[space.id]?.lapsed_subscription,
    [spaceAccess],
  );

  // Only a question that loaded can be answered, so the locked card offers it once its field is in the layout.
  const profileFieldIdFor = useCallback(
    (space: SpaceAccessTarget) => {
      const profileFieldId = spaceAccess?.[space.id]?.profile_field_id;
      return route?.profileFields?.some((field) => field.id === profileFieldId) ? profileFieldId : undefined;
    },
    [spaceAccess, route?.profileFields],
  );

  // A viewer has to be able to see a space before "can you post here" means
  // anything, so space access and the read-only rule are answered together.
  const canPostIn = useCallback(
    (space: SpacePostTarget | null) =>
      canAccessSpace(space) && !(space && isSpacePreviewable(space)) && permissions.canPostInSpace(space),
    [canAccessSpace, isSpacePreviewable, permissions],
  );

  const feedFirstEnabled = route?.community?.feed_first_enabled === true;
  const canPostToFeed = feedFirstEnabled && permissions.canCreatePost;
  const canPostAnywhere = useMemo(
    () => canPostToFeed || (route?.spaces ?? []).some(canPostIn),
    [canPostToFeed, route?.spaces, canPostIn],
  );

  return {
    canAccessSpace,
    isSpaceClosed,
    isSpaceNew,
    isSpaceUnlisted,
    isSpacePlaced,
    isSpacePreviewable,
    listsMembers,
    isSpaceConditionLocked,
    lapsedSubscriptionFor,
    profileFieldIdFor,
    canPostIn,
    canPostAnywhere,
    feedFirstEnabled,
  };
};
